import { useAccount, useBalance, useReadContract } from "wagmi";
import { formatEther } from "viem";
import { gaiaAbi } from "../lib/abi";
import { GAIA_ADDRESS, DEFAULT_CHAIN_ID, isDeployed } from "../lib/contract";

/**
 * Dogeshit-style status dashboard — bold, loud, impossible to miss.
 * Shows wallet, delegation, bag, and mint status.
 */
export function StatusPanel() {
  const { address, isConnected } = useAccount();
  const chainId = DEFAULT_CHAIN_ID;
  const contract = GAIA_ADDRESS[chainId];
  const deployed = isDeployed(chainId);

  const { data: bal } = useBalance({ address, chainId, query: { enabled: !!address } });

  const { data: mintsUsedBN } = useReadContract({
    chainId,
    address: contract,
    abi: gaiaAbi,
    functionName: "mintsBy",
    args: address ? [address] : undefined,
    query: { enabled: !!address && deployed, refetchInterval: 12_000 },
  });
  const mintsUsed = mintsUsedBN !== undefined ? Number(mintsUsedBN as bigint) : 0;

  const { data: gaiaBalBN } = useReadContract({
    chainId,
    address: contract,
    abi: gaiaAbi,
    functionName: "balanceOf",
    args: address ? [address] : undefined,
    query: { enabled: !!address && deployed, refetchInterval: 12_000 },
  });
  const gaiaBal = gaiaBalBN !== undefined ? Number(formatEther(gaiaBalBN as bigint)) : 0;

  const ethBal = bal ? `${Number(bal.formatted).toFixed(4)} ${bal.symbol}` : "—";

  return (
    <section id="status" className="relative px-5 md:px-10 py-16 md:py-24 max-w-7xl mx-auto">
      <div className="rounded-2xl border border-amber-400/30 overflow-hidden bg-black/60">
        {/* Header */}
        <div className="px-6 py-5 border-b border-amber-400/20 flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <span className="inline-block w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
            <span className="font-display text-2xl md:text-3xl tracking-tighter text-amber-300">
              UR STATUS
            </span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400/60">
            {deployed ? "BASE MAINNET LIVE" : "OFFLINE"}
          </span>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-amber-400/10">
          <Stat label="WALLET" value={isConnected && address ? short(address) : "NOT CONNECTED"} dim={!isConnected} />
          <Stat label="DELEGATED TO" value={isConnected ? "AI ENGINE" : "—"} dim={!isConnected} />
          <Stat label="UR BAG" value={gaiaBal > 0 ? `${gaiaBal.toLocaleString(undefined, { maximumFractionDigits: 0 })} GAIA` : ethBal} />
          <Stat label="PUBLIC MINT" value="0.001 ETH / SLOT" sub={`${mintsUsed} / 10 slots`} />
        </div>

        {/* Footer message + action */}
        <div className="px-6 py-6 border-t border-amber-400/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-bone-dim text-center md:text-left leading-relaxed max-w-xl">
            Sign 1 time and give ur wallet to AI to mint over MCP protocol a coin for ur home.
            No gas. No forms. Just delegate and let Claude handle the rest.
          </p>
          {isConnected ? (
            <a
              href="#ai-mint"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-sm uppercase tracking-wider hover:brightness-110 transition"
            >
              Mint GAIA
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M3 8 H13 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-amber-400/30 text-amber-300 font-mono text-xs uppercase tracking-wider">
              Connect wallet above
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, dim }: { label: string; value: string; sub?: string; dim?: boolean }) {
  return (
    <div className={`px-5 py-6 ${dim ? "opacity-50" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-400/50 mb-2">{label}</div>
      <div className="font-display text-lg md:text-xl font-bold text-bone truncate">{value}</div>
      {sub && <div className="font-mono text-[10px] text-bone-dim/60 mt-1">{sub}</div>}
    </div>
  );
}

function short(a: string) { return `${a.slice(0, 6)}…${a.slice(-4)}`; }
